import { useNavigate } from '@tanstack/react-router';
import { Columns3, Download, List } from 'lucide-react';
import { useCallback, useEffect, useMemo, type ReactNode } from 'react';
import {
  PIPELINE_COLUMNS,
  PIPELINE_HIDDEN_BY_DEFAULT,
  activeFilterCount,
  type ApplicationStage,
  type JobDetail,
  type PipelineColumn,
  type PipelineLayout,
  type PipelineStageView,
  type SavedViewRow,
  type SavedViewState,
} from '@nest-recruit/shared';
import { useTablePreferences } from '../../../components/data-table/use-table-preferences';
import { ForbiddenState, ServerErrorState } from '../../../components/system-states/SystemState';
import { errorMessage } from '../../../lib/api-client';
import { useNow } from '../../../lib/use-now';
import { registerCommands } from '../../../shell/command-registry';
import { useRoom } from '../../../shell/SocketProvider';
import { useToasts } from '../../../stores/toasts';
import { useJobOwners } from '../../jobs/api/jobs-api';
import { downloadExport, usePipelineBoard, usePipelineList, usePipelineOptions } from '../api/pipeline-api';
import { ActionReport } from '../components/ActionReport';
import { PipelineBoard } from '../components/board/PipelineBoard';
import { pipelineColumns } from '../components/pipeline-columns';
import { PipelineList } from '../components/PipelineList';
import { PipelineToolbar } from '../components/PipelineToolbar';
import { usePipelineActions } from '../hooks/use-pipeline-actions';
import { filterOf, withFilters, type FilterChanges, type PipelineSearch } from '../pipeline-search';
import styles from './PipelineTab.module.css';
import fit from '../../../components/FitPage.module.css';
import { useModule, useStageLabels } from '../../../lib/modules';

const MINUTE_MS = 60_000;

interface PipelineTabProps {
  readonly detail: JobDetail;
  /** The filters, page and layout, read from whichever address shows the list (06-qol §6). */
  readonly search: PipelineSearch;
  readonly onSearch: (next: PipelineSearch) => void;
  /** A JOB-12 stage (R2): the list shows only these stages, comma-separated, as a list. */
  readonly lockedStage?: string;
}

/**
 * SCR-02 (list) and SCR-03 (board) over one address, so the filters survive a switch
 * between them and a copied link opens either exactly as it was (06-qol §6). Shown whole
 * from JOB-12's ⋯ menu, and locked to one stage under each stage of the bar (R2). While it
 * is open the job's room is joined: a move on a colleague's board refetches this one (ADR 0003).
 */
export function PipelineTab({ detail, search, onSearch, lockedStage }: PipelineTabProps): ReactNode {
  const stageLabels = useStageLabels();
  const jobId = detail.id;
  const navigate = useNavigate();
  const show = useToasts((state) => state.show);
  const isStageLocked = lockedStage !== undefined;
  const layout: PipelineLayout = isStageLocked ? 'list' : (search.layout ?? 'list');
  const filter = useMemo(() => (lockedStage === undefined ? filterOf(search) : { ...filterOf(search), stage: lockedStage }), [search, lockedStage]);
  const now = useNow(MINUTE_MS);

  useRoom(`job:${jobId}`);
  const options = usePipelineOptions();
  const owners = useJobOwners(jobId);
  const actions = usePipelineActions(jobId);
  const list = usePipelineList(jobId, filter, search.page ?? 1, layout === 'list');
  const board = usePipelineBoard(jobId, filter, layout === 'board');

  const labels = useMemo(() => labelsOf(list.data?.stages ?? board.data?.columns ?? []), [list.data, board.data]);
  const isDaysShown = useModule('time_metrics');
  const columns = useMemo(() => pipelineColumns({ labels, pending: actions.pending, now, isDaysShown }), [labels, actions.pending, now, isDaysShown]);
  const preferences = useTablePreferences('pipeline-list', columns, PIPELINE_HIDDEN_BY_DEFAULT);

  const setSearch = onSearch;
  const onFilter = useCallback((changes: FilterChanges) => setSearch(withFilters(search, changes)), [setSearch, search]);
  const setLayout = useCallback(
    (next: PipelineLayout) => {
      const { layout: _layout, ...rest } = search;
      setSearch(next === 'board' ? { ...rest, layout: 'board' } : rest);
    },
    [setSearch, search],
  );

  const hiddenColumns = [...preferences.hiddenColumns].filter(isColumn);
  const viewState: SavedViewState = { filters: filter, hiddenColumns, layout };

  function applyView(view: SavedViewRow): void {
    preferences.setHiddenColumns(view.state.hiddenColumns);
    // A view saved on the whole pipeline keeps its stages there; inside a stage, the stage wins.
    if (isStageLocked) {
      setSearch({ ...view.state.filters, view: view.id });
      return;
    }
    void navigate({ to: '/jobs/$jobId/pipeline', params: { jobId }, search: { ...view.state.filters, ...(view.state.layout === 'board' ? { layout: 'board' } : {}), view: view.id }, replace: true });
  }

  const fileName = `${detail.reference}-pipeline-${new Date(now).toISOString().slice(0, 10)}.csv`;
  const exportAll = useCallback(async (): Promise<void> => {
    const shown = columns.filter((column) => !preferences.hiddenColumns.has(column.id)).map((column) => column.id).filter(isColumn);
    try {
      await downloadExport(jobId, { selection: { kind: 'all-matching', filter, excluded: [] }, columns: shown }, fileName);
      show({ tone: 'success', message: 'Export ready', detail: 'Fields you cannot see are in the file as —.' });
    } catch (error) {
      show({ tone: 'danger', message: "Couldn't export", detail: errorMessage(error) });
    }
  }, [columns, preferences.hiddenColumns, jobId, filter, fileName, show]);

  // 06-qol §1: the screen's own actions come first in the palette while it is open.
  useEffect(
    () =>
      registerCommands([
        ...(isStageLocked
          ? []
          : [
              {
                id: `pipeline.layout.${jobId}`,
                label: layout === 'list' ? 'Show the pipeline as a board' : 'Show the pipeline as a list',
                section: 'action' as const,
                context: detail.publicTitle ?? detail.title,
                permission: 'application' as const,
                icon: layout === 'list' ? Columns3 : List,
                keywords: ['kanban', 'board', 'list', 'table', 'pipeline'],
                run: () => setLayout(layout === 'list' ? 'board' : 'list'),
              },
            ]),
        {
          id: `pipeline.export.${jobId}`,
          label: 'Export the pipeline',
          section: 'action',
          context: detail.publicTitle ?? detail.title,
          permission: 'application',
          icon: Download,
          keywords: ['csv', 'excel', 'download', 'spreadsheet'],
          run: () => void exportAll(),
        },
      ]),
    [jobId, layout, isStageLocked, detail.publicTitle, detail.title, setLayout, exportAll],
  );

  const current = layout === 'list' ? list : board;
  if (current.error?.status === 403) return <ForbiddenState resource="this job's applications" contact="Your admin" />;
  if (current.error && !current.data) return <ServerErrorState onRetry={() => void current.refetch()} />;

  const reportId = actions.reportId ?? search.action ?? null;
  const filterCount = activeFilterCount(filter) - (filter.stage ? 1 : 0);

  return (
    <div className={`${styles.tab} ${fit.fill}`}>
      <PipelineToolbar
        layout={layout}
        onLayout={setLayout}
        filter={filter}
        onFilter={onFilter}
        onClear={() => setSearch({ ...(search.layout ? { layout: search.layout } : {}) })}
        stages={list.data?.stages ?? []}
        owners={owners.data ?? []}
        tags={options.data?.tags ?? []}
        viewId={search.view}
        viewState={viewState}
        onApplyView={applyView}
        onExportAll={() => void exportAll()}
        isStageLocked={isStageLocked}
      />

      {reportId !== null && (
        <ActionReport
          key={reportId}
          actionId={reportId}
          onUndo={(id) => void actions.undo(id)}
          onFinished={actions.announce}
          onClose={() => {
            actions.setReportId(null);
            if (search.action) {
              const { action: _action, ...rest } = search;
              setSearch(rest);
            }
          }}
        />
      )}

      {layout === 'list' ? (
        <PipelineList
          jobId={jobId}
          fileName={fileName}
          search={search}
          onSearch={setSearch}
          data={list.data}
          isLoading={list.isPending}
          options={options.data}
          owners={owners.data ?? []}
          testName={detail.assessment.isOn ? (detail.assessment.test?.name ?? null) : null}
          isPaperTest={detail.assessment.test?.isOffline ?? false}
          actions={actions}
          columns={columns}
          preferences={preferences}
          filterCount={filterCount}
          onClearFilters={() => setSearch({})}
          lockedStage={lockedStage}
        />
      ) : (
        <PipelineBoard jobId={jobId} data={board.data} isLoading={board.isPending} options={options.data} actions={actions} stageLabel={(stage) => labels.get(stage) ?? stageLabels[stage]} />
      )}
    </div>
  );
}

function labelsOf(stages: readonly PipelineStageView[]): ReadonlyMap<ApplicationStage, string> {
  return new Map(stages.map((stage) => [stage.stage, stage.label]));
}

function isColumn(value: string): value is PipelineColumn {
  return (PIPELINE_COLUMNS as readonly string[]).includes(value);
}
